import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { DiceIcon, EnterIcon, ExitIcon, PlayersIcon } from '@/features/lobby/icons'
import { Avatar } from './Avatar'
import { logout } from './api'
import type { UserCard } from './types'
import { meKey } from './useAuth'

type Props = {
  me: UserCard | null
  pending: number
  go: (path: string) => void
}

/**
 * Учётная запись в шапке — одной кнопкой.
 *
 * Пунктов было четыре: друзья, ник, выйти и тема. На телефоне они не
 * вставали в строку и переносились вторым рядом — считанная ширина на 360
 * пикселях: марка 29 + правила 36 + друзья 36 + ник 104 + выйти 36 + тема 36
 * + звук 36 и промежутки, всего 345 при 319 доступных на игровом экране (там
 * шапка ещё и с подложкой, а значит с полями). В меню ряд помещался, в
 * партии — нет, и жалоба была ровно про партию.
 *
 * Резать было нечего: каждый пункт уже был значком без подписи. Поэтому
 * вместо ряда — одна кнопка с аватаркой, а под ней список словами. Слова
 * вернулись: «друзья» и «выйти» значком не объяснить, и раньше их
 * приходилось угадывать.
 *
 * Тема сюда не попала и не должна: у неё, громкости и вида поля один дом —
 * страница настроек, а в шапку от неё ведёт шестерёнка рядом.
 *
 * Не вошедшему меню не заводится: складывать в него нечего — настройки
 * достаются шестерёнкой, и остаётся одна кнопка «войти».
 *
 * Раскрывающийся список, а не role="menu": роль меню обещает читалке
 * стрелки вверх-вниз, а их тут нет и заводить их ради четырёх пунктов не за
 * чем. Обычные кнопки обходятся Tab'ом, который работает сам, а про
 * раскрытие говорит aria-expanded на кнопке.
 */
export function AuthMenu({ me, pending, go }: Props) {
  const client = useQueryClient()
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  const leave = useMutation({
    mutationFn: logout,
    onSuccess: async () => {
      client.setQueryData(meKey, null)
      // Друзья и приглашения предыдущего человека новому не показываются.
      await client.invalidateQueries()
      go('/')
    },
  })

  /*
   * Закрыть меню: нажатием мимо и клавишей Escape.
   *
   * pointerdown, а не click: на нажатие внутри меню отвечает сама кнопка
   * пункта, и к моменту click её уже нет в документе — closest() не нашёл бы
   * меню, и обработчик закрывал бы его прежде, чем пункт сработает.
   */
  useEffect(() => {
    if (!open) return

    const away = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false)
    }
    const shut = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('pointerdown', away)
    document.addEventListener('keydown', shut)

    return () => {
      document.removeEventListener('pointerdown', away)
      document.removeEventListener('keydown', shut)
    }
  }, [open])

  if (me === null) {
    /*
     * Со значком, как и остальные пункты шапки: на узком экране от подписей
     * остаются значки, и «войти» словом было единственным, что оставалось
     * широким. На 320 пикселях этой разницы хватало, чтобы ряд перестал
     * влезать, — считанные числа в комментарии выше.
     */
    return (
      <button className="ghost" type="button" onClick={() => go('/login')} aria-label="войти">
        <span className="ghost__icon" aria-hidden="true">
          <EnterIcon />
        </span>
        <span className="ghost__label">войти</span>
      </button>
    )
  }

  const name = me.nickname ?? me.name
  // Пункт меню закрывает меню сам: уходя на другой экран, открытым его
  // оставлять нельзя — он повиснет поверх нового.
  const pick = (run: () => void) => () => {
    setOpen(false)
    run()
  }

  return (
    <div className="headmenu" ref={box}>
      <button
        className="ghost headmenu__trigger"
        type="button"
        aria-expanded={open}
        aria-label={name}
        onClick={() => setOpen(!open)}
      >
        <Avatar name={name} src={me.avatar} />
        {/* Счётчик заявок — на самой кнопке: про заявку иначе не узнать,
            не открыв меню, а открывают его не за этим. */}
        {pending > 0 && <span className="chip chip--turn headmenu__count">{pending}</span>}
      </button>

      {open && (
        <div className="headmenu__list">
          <button className="headmenu__item" type="button" onClick={pick(() => go('/me'))}>
            <Avatar name={name} src={me.avatar} />
            <span className="headmenu__name">{name}</span>
          </button>

          <button className="headmenu__item" type="button" onClick={pick(() => go('/friends'))}>
            <span className="headmenu__icon" aria-hidden="true">
              <PlayersIcon />
            </span>
            друзья
            {pending > 0 && <span className="chip chip--turn headmenu__badge">{pending}</span>}
          </button>

          {/* Магазин — отсюда, а не только из карточки заданий: карточка
              живёт в лобби, а заглядывают в магазин и посреди партии. */}
          <button className="headmenu__item" type="button" onClick={pick(() => go('/shop'))}>
            <span className="headmenu__icon" aria-hidden="true">
              <DiceIcon />
            </span>
            магазин
          </button>

          <button className="headmenu__item" type="button" onClick={pick(() => leave.mutate())}>
            <span className="headmenu__icon" aria-hidden="true">
              <ExitIcon />
            </span>
            выйти
          </button>
        </div>
      )}
    </div>
  )
}
